import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Stack,
  CircularProgress,
  Tooltip,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import CodeEditor from "../common/code-editor";

import { SAND_CAT } from "../config";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import { useRecoilState } from "recoil";
import { developerAccessTokenState } from "../global-state";
import AddIcon from "@mui/icons-material/Add";
import useApiCall from "../common/api-call";
import ComponentPreview from "../common/component-preview";
import WidgetEditor from "./widget-editor";

const CustomForm = {
  ObjectFieldTemplate: props => {
    return (
      <Grid container spacing={3} alignItems="stretch">
        {props.properties.map((prop, idx) => (
          <Grid item xs={12} sm={6} md={4} key={prop.name || idx}>
            <Box
              sx={{
                p: 2,
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                minHeight: 120,
              }}
            >
              {prop.content}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  },

  FieldTemplate: props => {
    const { id, label, help, required, description, errors, children } = props;
    const fieldId =
      props.idSchema?.$id || id || Math.random().toString(36).slice(2);

    return (
      <Box key={`field-${fieldId}`} sx={{ width: "100%" }}>
        {label && (
          <Typography
            component="label"
            htmlFor={fieldId}
            sx={{
              display: "block",
              mb: 1,
              fontWeight: required ? 500 : 400,
              color: "text.primary",
            }}
          >
            {label}
            {required && (
              <Typography
                component="span"
                sx={{ color: "error.main", ml: 0.5 }}
              >
                *
              </Typography>
            )}
          </Typography>
        )}
        {description && (
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, color: "text.secondary" }}
          >
            {description}
          </Typography>
        )}
        <div>{children}</div>
        {errors && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, color: "error.main" }}
          >
            {errors}
          </Typography>
        )}
        {help && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, color: "info.main" }}
          >
            {help}
          </Typography>
        )}
      </Box>
    );
  },

  ArrayFieldTemplate: props => {
    return (
      <Box sx={{ mb: 2 }}>
        {props.title && (
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            {props.title}
          </Typography>
        )}
        {props.description && (
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {props.description}
          </Typography>
        )}
        {props.items.map((element, idx) => (
          <Box
            key={element.key}
            sx={{ display: "flex", alignItems: "center", mb: 1 }}
          >
            <Box sx={{ flex: 1 }}>{element.children}</Box>
            {element.hasRemove && (
              <IconButton
                onClick={element.onDropIndexClick(element.index)}
                color="error"
                size="small"
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
        {props.canAdd && (
          <Button
            onClick={props.onAddClick}
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Pattern
          </Button>
        )}
      </Box>
    );
  },
};

const defaultSkin = {
  id: "",
  theme: "default",
  name: "",
  patterns: "*",
  detailOnly: false,
  defaultLayout: {
    w: 8,
    h: 4,
  },
  version: "official",
  provider: "developer",
  componentCode: "",
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const EllipsisTooltipCell = ({ value, width = 150 }) => (
  <Tooltip
    title={<span style={{ whiteSpace: "pre-line" }}>{value}</span>}
    placement="top-start"
  >
    <Typography
      variant="body2"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "pre-line",
        maxWidth: width,
        display: "block",
      }}
    >
      {typeof value === "string"
        ? value.split("\n").map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              {idx !== value.split("\n").length - 1 && <br />}
            </React.Fragment>
          ))
        : value}
    </Typography>
  </Tooltip>
);

function SkinConfig() {
  const { apiCall } = useApiCall();
  const [skins, setSkins] = useState([]);
  const [filteredSkins, setFilteredSkins] = useState([]);
  const [selectedSkin, setSelectedSkin] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState(defaultSkin);
  const [widgetData, setWidgetData] = useState({});

  const [isCompiled, setIsCompiled] = useState(false);
  const [hasCompiled, setHasCompiled] = useState(false);

  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const { successToast, errorToast } = useToast();
  const [developerAccessToken] = useRecoilState(developerAccessTokenState);
  const [isRunning, setIsRunning] = useState(null);
  const [uiSchema, setUiSchema] = useState({
    "ui:submitButtonOptions": {
      norender: true,
    },
    id: {
      "ui:disabled": true,
    },
    patterns: {
      "ui:options": {
        addable: true,
        removable: true,
        orderable: false,
      },
      items: {
        "ui:widget": "text",
        "ui:options": {
          placeholder: "Enter pattern (use * for wildcard)",
        },
      },
    },
    defaultLayout: {
      "ui:title": false,
      "ui:order": ["w", "h"],
      w: {
        "ui:widget": "updown",
        "ui:options": {
          id: "layout-width-input",
          style: {
            textAlign: "center",
          },
        },
      },
      h: {
        "ui:widget": "updown",
        "ui:options": {
          id: "layout-height-input",
          style: {
            textAlign: "center",
          },
        },
      },
    },
    version: {
      "ui:widget": "text",
      "ui:options": {
        readOnly: true,
        inputProps: {
          style: {
            backgroundColor: "#f5f5f5",
            cursor: "not-allowed",
          },
        },
      },
    },
    componentCode: {
      type: "string",
      title: "Component Code",
      description: "Custom React component code (JSX/TSX)",
      default: "",
    },
  });

  const baseSchema = {
    type: "object",
    required: ["id", "theme", "name", "patterns"],
    properties: {
      id: {
        type: "string",
        title: "Skin ID",
        description: "Unique identifier for the skin",
        $id: "skin-id-field",
        readOnly: true,
      },
      name: {
        type: "string",
        title: "Skin Name",
        description: "Display name for the skin",
        $id: "skin-name-field",
      },
      theme: {
        type: "string",
        title: "Theme Type",
        description: "Visual theme for the skin",
        enum: ["default", "mini"],
        default: "default",
        $id: "skin-theme-field",
      },
      patterns: {
        type: "array",
        title: "Match Patterns",
        description: "Patterns for skin matching (use * for wildcard)",
        items: {
          type: "string",
        },
        default: ["*"],
        $id: "skin-patterns-field",
      },
      detailOnly: {
        type: "boolean",
        title: "Detail Only",
        description: "Hide layout configuration when enabled",
        default: false,
        $id: "skin-detail-only-field",
      },
      defaultLayout: {
        type: "object",
        title: "Default Layout Settings",
        description: "Define the default size of the skin",
        $id: "skin-layout-container",
        properties: {
          w: {
            type: "integer",
            title: "Width",
            description: "Width in grid units (1-4)",
            minimum: 1,
            maximum: 4,
            default: 1,
            $id: "skin-layout-width",
          },
          h: {
            type: "integer",
            title: "Height",
            description: "Height in grid units (1-4)",
            minimum: 1,
            maximum: 4,
            default: 1,
            $id: "skin-layout-height",
          },
        },
      },
      componentCode: {
        type: "string",
        title: "Component Code",
        description: "Custom React component code (JSX/TSX)",
        default: "",
      },
    },
  };
  const [currentSchema, setCurrentSchema] = useState(baseSchema);

  // Generate dynamic schema based on detailOnly flag
  const getDynamicSchema = formData => {
    const schema = { ...baseSchema };

    if (formData.detailOnly) {
      // Remove defaultLayout from schema when detailOnly is true
      const { defaultLayout, ...propertiesWithoutLayout } = schema.properties;
      schema.properties = propertiesWithoutLayout;
    }

    return schema;
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: params => <EllipsisTooltipCell value={params.value} />,
      minWidth: 150,
    },
    {
      field: "id",
      headerName: "ID",
      flex: 1,
      renderCell: params => <EllipsisTooltipCell value={params.value} />,
      minWidth: 150,
    },
    {
      field: "patterns",
      headerName: "Patterns",
      flex: 1,
      renderCell: params => {
        const value = params.value;
        if (Array.isArray(value)) {
          return (
            <EllipsisTooltipCell
              value={value.length > 0 ? value.join("\n") : ""}
              width={200}
            />
          );
        }
        return <EllipsisTooltipCell value={value} width={200} />;
      },
      minWidth: 100,
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 100,
      valueGetter: params => params.row.version || "prod",
    },
    {
      field: "theme",
      headerName: "Theme",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "provider",
      headerName: "Provider",
      flex: 1,
      minWidth: 100,
      valueGetter: params => params.row.provider || "mlink",
    },
    {
      field: "detailOnly",
      headerName: "Detail Only",
      flex: 1,
      minWidth: 100,
      valueGetter: params => (params.row.detailOnly ? "Yes" : "No"),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: params => {
        const skin = params.row;
        return (
          <Stack direction="row" spacing={1}>
            {skin.version === "stg" && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => {
                  setSelectedSkin(skin);
                  setPublishDialogOpen(true);
                }}
              >
                Publish
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={e => {
                e.stopPropagation();
                setSelectedSkin(skin);
                setDeleteDialogOpen(true);
              }}
            >
              Delete
            </Button>
          </Stack>
        );
      },
    },
  ];

  useEffect(() => {
    setIsCompiled(false);
    setHasCompiled(false);
  }, [formData.componentCode, widgetData]);

  useEffect(() => {
    fetchSkinList();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredSkins(skins);
      return;
    }

    const filtered = skins.filter(skin =>
      skin.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredSkins(filtered);
  }, [searchTerm, skins]);

  const fetchSkinList = async () => {
    setLoading(true);
    const statusUrl = `${SAND_CAT}/ui/cardSkinConfig/list`;
    try {
      const statusData = await apiCall("/ui/cardSkinConfig/list", "GET");
      if (statusData) {
        setSkins(statusData);
        setFilteredSkins(statusData);
      } else {
        setSkins([]);
        setFilteredSkins([]);
      }
    } catch (error) {
      errorToast("Error fetching data! Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSkinDetail = async (skinId, version) => {
    try {
      const queryParams = new URLSearchParams({
        skinId: skinId,
        version: version,
      });

      const response = await fetch(
        `${SAND_CAT}/ui/cardSkinConfig/get?${queryParams}`,
        {
          method: "GET",
          headers: {
            DeveloperAuthorization: `Bearer ${developerAccessToken}`,
          },
        },
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data);
      }
      return data;
    } catch (error) {
      errorToast("Error fetching skin details");
      console.error(error);
      return null;
    }
  };

  const saveSkin = async skinData => {
    const response = await fetch(`${SAND_CAT}/ui/cardSkinConfig/upsert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        DeveloperAuthorization: `Bearer ${developerAccessToken}`,
      },
      body: JSON.stringify(skinData),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data);
    }
    return data;
  };

  const deleteSkin = async skinData => {
    const queryParams = {
      id: skinData.id,
      version: skinData.version,
    };
    const response = await fetch(`${SAND_CAT}/ui/cardSkinConfig/delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        DeveloperAuthorization: `Bearer ${developerAccessToken}`,
      },
      body: JSON.stringify(queryParams),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data);
    }
    return data;
  };

  const publishSkin = async skinData => {
    const queryParams = {
      id: skinData.id,
      version: skinData.version,
    };
    const response = await fetch(`${SAND_CAT}/ui/cardSkinConfig/publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        DeveloperAuthorization: `Bearer ${developerAccessToken}`,
      },
      body: JSON.stringify(queryParams),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data);
    }
    return data;
  };

  const reloadSkin = async () => {
    const response = await fetch(`${SAND_CAT}/ui/cardSkinConfig/reload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        AdminAuthorization: `Bearer ${developerAccessToken}`,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data);
    }
    return data;
  };

  const handleSkinSelect = async skin => {
    setLoading(true);
    try {
      const skinDetail = await fetchSkinDetail(skin.id, skin.version);
      if (skinDetail) {
        setSelectedSkin(skinDetail);
        setFormData(skinDetail);

        const editSchema = {
          ...baseSchema,
          properties: {
            ...baseSchema.properties,
            id: {
              ...baseSchema.properties.id,
              readOnly: true,
              description:
                "Unique identifier for the skin (cannot be modified in edit mode)",
            },
            version: {
              type: "string",
              title: "Version",
              description: "Version of the skin",
              default: "official",
              $id: "skin-version-field",
            },
          },
        };

        // Apply detailOnly logic to edit schema
        if (skinDetail.detailOnly) {
          const { defaultLayout, ...propertiesWithoutLayout } =
            editSchema.properties;
          editSchema.properties = propertiesWithoutLayout;
        }

        setCurrentSchema(editSchema);

        setUiSchema(prevSchema => ({
          ...prevSchema,
          id: {
            "ui:disabled": true,
            "ui:readonly": true,
            "ui:widget": "text",
            "ui:options": {
              inputProps: {
                style: {
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                },
              },
            },
          },
        }));

        setTabValue(0);
        setDialogOpen(true);
      }
    } catch (error) {
      errorToast("Error fetching skin details! Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewSkin = () => {
    setSelectedSkin(null);
    setFormData({ ...defaultSkin });

    setCurrentSchema(getDynamicSchema(defaultSkin));

    setUiSchema(prevSchema => ({
      ...prevSchema,
      id: {
        "ui:disabled": false,
        "ui:readonly": false,
        "ui:widget": "text",
      },
    }));

    setTabValue(0);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
    setPublishDialogOpen(false);
    setUiSchema(prevSchema => ({
      ...prevSchema,
      version: {
        "ui:widget": "text",
        "ui:options": {
          readOnly: true,
          inputProps: {
            style: {
              backgroundColor: "#f5f5f5",
              cursor: "not-allowed",
            },
          },
        },
      },
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.id || !formData.name) {
        showSnackbar("ID and Name are required fields", "error");
        return;
      }

      setIsRunning("save");
      const result = await saveSkin(formData);
      if (result) {
        successToast("Save Success");
        await fetchSkinList();
        setDialogOpen(false);
      }
    } catch (error) {
      errorToast("Error");
    } finally {
      setIsRunning(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedSkin) return;

    try {
      setIsRunning("delete");
      const result = await deleteSkin(selectedSkin);
      if (result.success) {
        successToast("Deleted Success");
      } else {
        errorToast("Error");
      }
      await fetchSkinList();
      setDeleteDialogOpen(false);
      setDialogOpen(false);
    } catch (error) {
      errorToast("Error");
    } finally {
      setIsRunning(null);
    }
  };

  const handlePublish = async () => {
    if (!selectedSkin) return;

    try {
      setIsRunning("publish");
      const result = await publishSkin(selectedSkin);
      if (result.success) {
        successToast("Publish Success");
      } else {
        errorToast("Error");
      }
      await fetchSkinList();
      setPublishDialogOpen(false);
      setDialogOpen(false);
    } catch (error) {
      errorToast("Error");
    } finally {
      setIsRunning(null);
    }
  };

  const handleFormChange = ({ formData: newFormData }) => {
    if (selectedSkin) {
      setFormData({
        ...newFormData,
        id: selectedSkin.id,
      });
    } else {
      setFormData(newFormData);
    }

    // Update schema when detailOnly changes
    setCurrentSchema(getDynamicSchema(newFormData));
  };

  const handleJsonChange = update => {
    let newFormData = update;

    if (update.updated_src) {
      newFormData = update.updated_src;
    }

    if (selectedSkin) {
      if (newFormData.id !== selectedSkin.id) {
        newFormData = {
          ...newFormData,
          id: selectedSkin.id,
        };
        errorToast("Skin ID cannot be modified in edit mode");
      }
    }

    setFormData(newFormData);
  };

  const handleReload = async () => {
    setIsRunning("reload");
    const result = await reloadSkin();
    if (result.success) {
      successToast("Reload Success");
    } else {
      errorToast("Error");
    }
    setIsRunning(null);
    fetchSkinList();
  };

  const handleCompile = () => {
    setHasCompiled(true);
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 2,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Box>
            <Button
              variant="contained"
              onClick={() => handleReload()}
              sx={{
                mb: 2,
                opacity: 1,
                "&.Mui-disabled": {
                  backgroundColor: theme => theme.palette.primary.main,
                  color: theme => theme.palette.primary.contrastText,
                },
              }}
            >
              reload
              {isRunning === "reload" && (
                <CircularProgress size={20} style={{ ml: 1 }} />
              )}
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Skin List</Typography>
            <Button
              variant="outlined"
              onClick={handleNewSkin}
              size="small"
              startIcon={<AddIcon />}
            >
              New Skin
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search skin name..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, width: "100%", mt: 2, overflow: "hidden" }}>
          <DataGrid
            rows={filteredSkins}
            columns={columns}
            getRowId={row => `${row.id}-${row.version || "prod"}`}
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableSelectionOnClick
            loading={loading}
            onRowClick={(params, event) => {
              if (!event.target.closest("button")) {
                handleSkinSelect(params.row);
              }
            }}
            components={{
              LoadingOverlay: LoadingSpinner,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                py: 1,
                outline: "none !important",
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        maxWidth="md"
        fullWidth
        open={dialogOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: "80vh",
            m: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography component="div" variant="h6">
            {selectedSkin ? "Edit Skin" : "New Skin"}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "grey.500" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
            scrollButtons="auto"
            variant="scrollable"
          >
            <Tab label="Form Editor" />
            <Tab label="JSON Editor" />
            <Tab label="Component Code" />
            <Tab label="Component Preview" />
          </Tabs>

          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
            <TabPanel value={tabValue} index={0}>
              <Box
                sx={{
                  maxWidth: "100%",
                  "& .rjsf": {
                    "& .MuiTextField-root": {
                      bgcolor: "background.paper",
                    },
                    "& select": {
                      width: "100%",
                      p: 1,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    },
                  },
                }}
              >
                <Form
                  schema={getDynamicSchema(formData)}
                  formData={formData}
                  validator={validator}
                  onChange={handleFormChange}
                  onSubmit={() => handleSave()}
                  templates={CustomForm}
                  idPrefix="skin-config"
                  idSeparator="-"
                  uiSchema={uiSchema}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box
                sx={{
                  bgcolor: "background.paper",
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "& .react-json-view": {
                    fontFamily: "monospace",
                    fontSize: "14px",
                    "& .object-key": {
                      color: "primary.main",
                    },
                    "& .string-value": {
                      color: "success.main",
                    },
                    "& .number-value": {
                      color: "warning.main",
                    },
                    "& .boolean-value": {
                      color: "error.main",
                    },
                    "& .null-value": {
                      color: "text.secondary",
                    },
                    "& .object-content": {
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      marginLeft: "8px",
                      paddingLeft: "8px",
                    },
                    "& .variable-row": {
                      padding: "4px 0",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": {
                        borderBottom: "none",
                      },
                    },
                    "& .object-key-val": {
                      padding: "4px 0",
                    },
                    "& .icon-container": {
                      marginRight: "4px",
                    },
                  },
                }}
              >
                <JsonView
                  src={formData}
                  onEdit={handleJsonChange}
                  onAdd={handleJsonChange}
                  onDelete={handleJsonChange}
                  theme="rjv-default"
                  displayDataTypes={false}
                  enableClipboard={true}
                  collapsed={1}
                  name={false}
                  style={{
                    fontFamily: "monospace",
                    fontSize: "14px",
                  }}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <CodeEditor
                value={formData.componentCode}
                editable={true}
                height="400px"
                onChange={instance => {
                  const code = instance.getValue();
                  setFormData({ ...formData, componentCode: code });
                }}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <WidgetEditor value={widgetData} onChange={setWidgetData} />
              <Button
                variant="contained"
                onClick={() => {
                  setHasCompiled(false);
                  setTimeout(() => {
                    setHasCompiled(true);
                    setIsCompiled(true);
                  }, 0);
                }}
                disabled={isCompiled}
                sx={{ mb: 2 }}
              >
                Compile
              </Button>
              <ComponentPreview
                code={formData.componentCode}
                widgetData={widgetData}
                hasCompiled={hasCompiled}
              />
            </TabPanel>
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 2 }}>
          {selectedSkin && (
            <>
              <Button
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isRunning && isRunning !== "delete"}
              >
                Delete
                {isRunning === "delete" && (
                  <CircularProgress size={20} style={{ ml: 1 }} />
                )}
              </Button>
              {selectedSkin.version === "stg" && (
                <Button
                  color="warning"
                  onClick={() => setPublishDialogOpen(true)}
                  disabled={isRunning && isRunning !== "publish"}
                >
                  Publish
                  {isRunning === "publish" && (
                    <CircularProgress size={20} style={{ ml: 1 }} />
                  )}
                </Button>
              )}
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ ml: 1, px: 3 }}
            disabled={isRunning && isRunning !== "save"}
          >
            Save
            {isRunning === "save" && (
              <CircularProgress size={20} style={{ ml: 1 }} />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this skin? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
      >
        <DialogTitle>Confirm Publish</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to publish this skin? This will make it
            available to all users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} color="warning">
            Publish
            {isRunning === "publish" && (
              <CircularProgress size={20} style={{ marginLeft: 10 }} />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SkinConfig;
